import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useIsAccountant } from '@/hooks/useRole';
import { Layout } from '@/components/layout/Layout';

// Lazy load pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const ClientsPage = lazy(() => import('@/pages/clients/ClientsPage'));
const ClientFormPage = lazy(() => import('@/pages/clients/ClientFormPage'));
const ClientDetailPage = lazy(() => import('@/pages/clients/ClientDetailPage'));
const SuppliersPage = lazy(() => import('@/pages/suppliers/SuppliersPage'));
const SupplierFormPage = lazy(() => import('@/pages/suppliers/SupplierFormPage'));
const SupplierDetailPage = lazy(() => import('@/pages/suppliers/SupplierDetailPage'));
const ProductsPage = lazy(() => import('@/pages/products/ProductsPage'));
const ProductFormPage = lazy(() => import('@/pages/products/ProductFormPage'));
const ProductDetailPage = lazy(() => import('@/pages/products/ProductDetailPage'));
const ExpensesPage = lazy(() => import('@/pages/expenses/ExpensesPage'));
const ExpenseFormPage = lazy(() => import('@/pages/expenses/ExpenseFormPage'));
const ExpenseDetailPage = lazy(() => import('@/pages/expenses/ExpenseDetailPage'));
const InvoicesPage = lazy(() => import('@/pages/invoices/InvoicesPage'));
const InvoiceFormPage = lazy(() => import('@/pages/invoices/InvoiceFormPage'));
const InvoiceDetailPage = lazy(() => import('@/pages/invoices/InvoiceDetailPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'));
const PlansPage = lazy(() => import('@/pages/plans/PlansPage'));
const QuotesPage = lazy(() => import('@/pages/quotes/QuotesPage'));
const QuoteFormPage = lazy(() => import('@/pages/quotes/QuoteFormPage'));
const QuoteDetailPage = lazy(() => import('@/pages/quotes/QuoteDetailPage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));
const AccountantDashboardPage = lazy(() => import('@/pages/accountant/AccountantDashboardPage'));
const AccountantClientsPage = lazy(() => import('@/pages/accountant/AccountantClientsPage'));
const AccountantPendingPage = lazy(() => import('@/pages/accountant/AccountantPendingPage'));
const AccountantClientDetailPage = lazy(() => import('@/pages/accountant/AccountantClientDetailPage'));

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Caricamento...</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const isAccountant = useIsAccountant();
  const { pathname: currentPath } = useLocation();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check if profile is complete
  const isProfileComplete = user?.profile?.country && user?.profile?.business_name;
  const isOnOnboardingPage = currentPath === '/onboarding';

  // Redirect to onboarding if profile incomplete (unless already there)
  if (!isProfileComplete && !isOnOnboardingPage) {
    return <Navigate to="/onboarding" replace />;
  }

  // Redirect to appropriate home if profile complete and on onboarding page
  if (isProfileComplete && isOnOnboardingPage) {
    return <Navigate to={isAccountant ? '/accountant' : '/dashboard'} replace />;
  }

  // Redirect accountants away from all user-only routes
  const accountantAllowedPaths = ['/accountant', '/accountant/clients', '/accountant/pending', '/settings', '/profile'];
  const isAllowedForAccountant = accountantAllowedPaths.some((p) => currentPath.startsWith(p));
  if (isAccountant && !isAllowedForAccountant) {
    return <Navigate to="/accountant" replace />;
  }

  return <>{children}</>;
}

// Public route (redirect to dashboard if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const isAccountant = useIsAccountant();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to={isAccountant ? '/accountant' : '/dashboard'} replace />;
  }

  return <>{children}</>;
}

// Redirects "/" to the correct home based on role
function HomeRedirect() {
  const isAccountant = useIsAccountant();
  return <Navigate to={isAccountant ? '/accountant' : '/dashboard'} replace />;
}

// Guard: prevents non-accountants from accessing accountant routes
function AccountantRoute({ children }: { children: React.ReactNode }) {
  const isAccountant = useIsAccountant();
  const { isLoading } = useAuth();

  if (isLoading) return <PageLoader />;
  if (!isAccountant) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function NonAccountantRoute({ children }: { children: React.ReactNode }) {
  const isAccountant = useIsAccountant();
  const { isLoading } = useAuth();

  if (isLoading) return <PageLoader />;
  if (isAccountant) return <Navigate to="/accountant" replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        {/* Reset password: standalone — must be accessible regardless of auth state
            (Supabase recovery link lands here with an active session) */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Legal pages: fully public, accessible regardless of auth state */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />

        {/* Onboarding route (protected, no layout) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes with Layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/" element={<HomeRedirect />} />
                  
                  {/* Client management */}
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/clients/new" element={<ClientFormPage />} />
                  <Route path="/clients/:id" element={<ClientDetailPage />} />
                  <Route path="/clients/:id/edit" element={<ClientFormPage />} />
                  
                  {/* Supplier management */}
                  <Route path="/suppliers" element={<SuppliersPage />} />
                  <Route path="/suppliers/new" element={<SupplierFormPage />} />
                  <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
                  <Route path="/suppliers/:id/edit" element={<SupplierFormPage />} />
                  
                  {/* Profile page */}
                  <Route path="/profile" element={<ProfilePage />} />
                  
                  {/* Product management */}
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/new" element={<ProductFormPage />} />
                  <Route path="/products/:id" element={<ProductDetailPage />} />
                  <Route path="/products/:id/edit" element={<ProductFormPage />} />
                  
                  {/* Expense management */}
                  <Route path="/expenses" element={<ExpensesPage />} />
                  <Route path="/expenses/new" element={<ExpenseFormPage />} />
                  <Route path="/expenses/:id" element={<ExpenseDetailPage />} />
                  <Route path="/expenses/:id/edit" element={<ExpenseFormPage />} />
                  
                  {/* Quote management */}
                  <Route path="/quotes" element={<QuotesPage />} />
                  <Route path="/quotes/new" element={<QuoteFormPage />} />
                  <Route path="/quotes/:id" element={<QuoteDetailPage />} />
                  <Route path="/quotes/:id/edit" element={<QuoteFormPage />} />

                  {/* Invoice management */}
                  <Route path="/invoices" element={<InvoicesPage />} />
                  <Route path="/invoices/new" element={<InvoiceFormPage />} />
                  <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
                  <Route path="/invoices/:id/edit" element={<InvoiceFormPage />} />
                  
                  {/* Plans & Pricing — not accessible to accountants */}
                  <Route path="/plans" element={<NonAccountantRoute><PlansPage /></NonAccountantRoute>} />
                  
                  {/* Settings */}
                  <Route path="/settings" element={<SettingsPage />} />

                  {/* Accountant portal */}
                  <Route
                    path="/accountant"
                    element={
                      <AccountantRoute>
                        <AccountantDashboardPage />
                      </AccountantRoute>
                    }
                  />
                  <Route
                    path="/accountant/clients"
                    element={
                      <AccountantRoute>
                        <AccountantClientsPage />
                      </AccountantRoute>
                    }
                  />
                  <Route
                    path="/accountant/pending"
                    element={
                      <AccountantRoute>
                        <AccountantPendingPage />
                      </AccountantRoute>
                    }
                  />
                  <Route
                    path="/accountant/clients/:clientId"
                    element={
                      <AccountantRoute>
                        <AccountantClientDetailPage />
                      </AccountantRoute>
                    }
                  />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}
